import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { CURRENCIES, EXCHANGE_RATES, ITEM_TYPES } from '../constants';
import LegoImage from './LegoImage';

function convertCurrency(price, fromCode, toCode, rates) {
    const oldRate = rates[fromCode] || 1;
    const newRate = rates[toCode] || 1;
    return (price / oldRate) * newRate;
}

function formatCurrency(value, code) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
    }).format(value || 0);
}

function highlight(text, term) {
    const value = text || '';
    if (!term) return value;
    const lower = value.toLowerCase();
    const index = lower.indexOf(term.toLowerCase());
    if (index < 0) return value;
    return (
        <>
            {value.slice(0, index)}
            <mark>{value.slice(index, index + term.length)}</mark>
            {value.slice(index + term.length)}
        </>
    );
}

function ItemRecords({ type, previousFilter, onFilterChange }) {
    const [items, setItems] = useState([]);
    const [exchangeRates, setExchangeRates] = useState(EXCHANGE_RATES);
    const [filterText, setFilterText] = useState(previousFilter.text || '');
    const [year, setYear] = useState(previousFilter.year || '');
    const [displayCurrency, setDisplayCurrency] = useState('USD');
    const [sort, setSort] = useState({
        field: 'date',
        direction: 'desc',
        type: 'date',
    });

    useEffect(() => {
        api.getItems()
            .then(setItems)
            .catch((error) => window.alert(error.message));
        api.getExchangeRates()
            .then(setExchangeRates)
            .catch(() => { /* keep hardcoded fallback */ });
    }, []);

    useEffect(() => {
        onFilterChange({ text: filterText, year });
    }, [filterText, year, onFilterChange]);

    const shownItems = useMemo(() => {
        const filtered = items
            .filter((item) => item.type === type)
            .filter((item) => {
                const term = filterText.toLowerCase();
                const textMatch =
                    (item.displayName || '').toLowerCase().includes(term) ||
                    (item.legoId || '').toLowerCase().includes(term) ||
                    (item.source || '').toLowerCase().includes(term) ||
                    (item.notes || '').toLowerCase().includes(term);

                const itemYear = new Date(item.date).getFullYear();
                const yearMatch = !year || itemYear === Number(year);
                return textMatch && yearMatch;
            })
            .map((item) => {
                const fromCode = CURRENCIES[item.currency] || 'USD';
                const convertedUnit = convertCurrency(
                    item.unitPrice,
                    fromCode,
                    displayCurrency,
                    exchangeRates,
                );
                return {
                    ...item,
                    convertedCurrency: displayCurrency,
                    convertedUnitPrice: convertedUnit,
                };
            });

        const sorted = [...filtered].sort((a, b) => {
            const aValue = a[sort.field];
            const bValue = b[sort.field];
            let result = 0;
            if (sort.type === 'date') {
                result = new Date(aValue) - new Date(bValue);
            } else if (sort.type === 'number') {
                result = Number(aValue) - Number(bValue);
            } else {
                result = String(aValue || '').localeCompare(
                    String(bValue || ''),
                    'zh',
                    {
                        numeric: true,
                        sensitivity: 'base',
                    },
                );
            }
            return sort.direction === 'asc' ? result : -result;
        });

        return sorted;
    }, [items, type, filterText, year, displayCurrency, sort, exchangeRates]);

    const total = useMemo(() => {
        return shownItems.reduce((sum, item) => {
            let line = item.convertedUnitPrice * item.quantity;
            if (type === ITEM_TYPES.Sell) {
                line *= 1 - item.fees / 100;
            }
            return sum + line;
        }, 0);
    }, [shownItems, type]);

    function onSort(field, valueType = 'string') {
        setSort((prev) => ({
            field,
            type: valueType,
            direction:
                prev.field === field && prev.direction === 'asc'
                    ? 'desc'
                    : 'asc',
        }));
    }

    const years = Array.from({ length: 5 }).map(
        (_, idx) => new Date().getFullYear() - idx,
    );

    return (
        <div>
            <div className='toolbar'>
                <div className='currency-group'>
                    {['USD', 'CNY', 'EUR'].map((code) => (
                        <button
                            key={code}
                            type='button'
                            className={
                                displayCurrency === code
                                    ? 'chip active'
                                    : 'chip'
                            }
                            onClick={() => setDisplayCurrency(code)}
                        >
                            {code === 'USD' ? '$' : code === 'CNY' ? '¥' : '€'}
                        </button>
                    ))}
                </div>
                <div className='total-text'>
                    Total {type === ITEM_TYPES.Buy ? 'cost' : 'income'}:{' '}
                    {formatCurrency(total, displayCurrency)}
                </div>
                <div className='toolbar-filters'>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                    >
                        <option value=''>by year...</option>
                        {years.map((y) => (
                            <option key={y} value={String(y)}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <input
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder='Filter...'
                    />
                </div>
            </div>

            <div className='table-wrap'>
                <table className='records-table'>
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th onClick={() => onSort('legoId')}>LegoId</th>
                            <th onClick={() => onSort('displayName')}>Name</th>
                            <th onClick={() => onSort('quantity', 'number')}>
                                Quantity
                            </th>
                            <th onClick={() => onSort('date', 'date')}>Date</th>
                            <th
                                onClick={() =>
                                    onSort('convertedUnitPrice', 'number')
                                }
                            >
                                Unit
                            </th>
                            <th>Total</th>
                            <th>Platform</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shownItems.map((item) => {
                            const unit =
                                type === ITEM_TYPES.Buy
                                    ? item.convertedUnitPrice
                                    : item.convertedUnitPrice *
                                      (1 - item.fees / 100);
                            const totalPrice = unit * item.quantity;
                            const itemDate =
                                item.date === '1970-01-01T00:00:00.000Z'
                                    ? ''
                                    : new Date(item.date).toLocaleDateString();

                            return (
                                <tr key={item.id}>
                                    <td data-label='Image'>
                                        <LegoImage legoId={item.legoId} />
                                    </td>
                                    <td data-label='Lego ID'>
                                        <Link to={`/mylego/${item.id}`}>
                                            {highlight(item.legoId, filterText)}
                                        </Link>
                                    </td>
                                    <td data-label='Name'>
                                        {highlight(
                                            item.displayName,
                                            filterText,
                                        )}
                                    </td>
                                    <td data-label='Quantity'>{item.quantity}</td>
                                    <td data-label='Date'>{itemDate}</td>
                                    <td data-label='Unit'>
                                        {formatCurrency(unit, displayCurrency)}
                                    </td>
                                    <td data-label='Total'>
                                        {formatCurrency(
                                            totalPrice,
                                            displayCurrency,
                                        )}
                                    </td>
                                    <td data-label='Platform'>
                                        {highlight(item.source, filterText)}
                                    </td>
                                    <td data-label='Notes'>
                                        {highlight(item.notes, filterText)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ItemRecords;
